<template>
  <div class="order-detail-tab order-detail-tab--checkin">
    <template v-if="!orderId">
      <a-empty description="暂无订单数据" />
    </template>
    <template v-else-if="loading">
      <a-spin tip="加载中..." />
    </template>
    <template v-else-if="!hasData">
      <a-empty description="暂无打卡记录" />
    </template>
    <div v-else class="tab-content">
      <div
        v-for="group in groupedByMaster"
        :key="group.master.id"
        class="checkin-master-block"
      >
        <div
          class="checkin-master-header"
          @click="toggleExpand(group.master.id)"
        >
          <a-avatar
            :src="group.master.avatar"
            :size="40"
          >
            {{ (group.master.nickname && group.master.nickname.charAt(0)) || (group.master.name && group.master.name.charAt(0)) || '师' }}
          </a-avatar>
          <div class="checkin-master-info">
            <span class="checkin-master-name">{{ group.master.nickname || group.master.name || '师傅' }}</span>
            <span v-if="group.master.mobile" class="checkin-master-mobile">{{ group.master.mobile }}</span>
          </div>
          <span class="checkin-master-expand">
            <span class="checkin-master-expand-text">{{ isExpanded(group.master.id) ? '收起' : '展开' }}</span>
            <a-icon :type="isExpanded(group.master.id) ? 'down' : 'right'" />
            <span class="checkin-master-count">共 {{ group.records.length }} 条</span>
          </span>
        </div>
        <div v-show="isExpanded(group.master.id)" class="checkin-master-body">
          <template v-if="group.records.length > 0">
            <div
              v-for="dateGroup in groupRecordsByDate(group.records)"
              :key="dateGroup.date"
              class="timeline-date-group"
            >
              <div class="timeline-line" />
              <div class="timeline-date-header">
                <span class="timeline-date-dot" />
                <span class="timeline-date-text">{{ dateGroup.date }}</span>
              </div>
              <div
                v-for="(item, rIdx) in dateGroup.records"
                :key="item.id || rIdx"
                class="timeline-record-item"
              >
                <span class="timeline-record-dot" :class="{ 'timeline-record-dot--warn': item.checkType === 2 || item.checkType === 3 }" />
                <div class="timeline-record-body">
                  <div class="timeline-record-row" :class="{ 'timeline-record-row--warn': item.checkType === 2 || item.checkType === 3 }">
                    <span class="timeline-record-type">{{ checkTypeText(item.checkType) }}</span>
                    <span class="timeline-record-time">{{ formatDateTime(item.checkTime) }}</span>
                    <span v-if="item.checkType === 3 && item.leaveEarlyStatus != null" class="timeline-record-status">{{ leaveEarlyStatusText(item.leaveEarlyStatus) }}</span>
                  </div>
                  <div v-if="item.checkType === 3 && item.leaveEarlyReason" class="timeline-record-reason">原因：{{ item.leaveEarlyReason }}</div>
                </div>
              </div>
            </div>
          </template>
          <a-empty v-else description="该师傅暂无打卡记录" :image="emptyImage" class="checkin-empty" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getOrderCheckRecords, getOrderMasters } from '@/api/order/order'

const CHECK_TYPE_MAP = {
  1: '上班',
  2: '迟到',
  3: '早退',
  4: '下班'
}
const LEAVE_EARLY_STATUS_MAP = {
  0: '未申请',
  1: '待审批',
  2: '已通过',
  3: '已拒绝'
}

export default {
  name: 'CheckInRecord',
  props: {
    orderId: { type: [Number, String], default: null }
  },
  data () {
    return {
      hasData: false,
      loading: false,
      masterList: [],
      recordList: [],
      expandedMasterIds: [],
      emptyImage: undefined
    }
  },
  computed: {
    /** 按师傅分组的打卡记录：[{ master, records }]，仅包含有打卡记录的师傅，并按师傅列表顺序 */
    groupedByMaster () {
      const list = []
      const recordMap = {}
      this.recordList.forEach(r => {
        const mid = r.masterOrderId
        if (!recordMap[mid]) recordMap[mid] = []
        recordMap[mid].push(r)
      })
      this.masterList.forEach(m => {
        const records = recordMap[m.id] || []
        list.push({ master: m, records })
      })
      return list
    }
  },
  watch: {
    orderId: {
      immediate: true,
      handler (id) {
        if (id) this.fetchAll()
        else {
          this.masterList = []
          this.recordList = []
          this.expandedMasterIds = []
          this.hasData = false
        }
      }
    }
  },
  methods: {
    isExpanded (masterId) {
      return this.expandedMasterIds.indexOf(masterId) !== -1
    },
    toggleExpand (masterId) {
      const idx = this.expandedMasterIds.indexOf(masterId)
      if (idx === -1) {
        this.expandedMasterIds = [...this.expandedMasterIds, masterId]
      } else {
        this.expandedMasterIds = this.expandedMasterIds.filter(id => id !== masterId)
      }
    },
    /** 按日期分组：[{ date: 'yyyy-MM-dd', records: [] }]，同一天内按时间正序 */
    groupRecordsByDate (records) {
      if (!records || records.length === 0) return []
      const map = {}
      const order = []
      const sorted = [...records].sort((a, b) => {
        const t1 = a.checkTime || a.createTime || ''
        const t2 = b.checkTime || b.createTime || ''
        return String(t1).localeCompare(String(t2))
      })
      sorted.forEach(item => {
        const dateStr = this.formatDateOnly(item.checkTime || item.createTime)
        if (!map[dateStr]) {
          map[dateStr] = []
          order.push(dateStr)
        }
        map[dateStr].push(item)
      })
      return order.map(date => ({ date, records: map[date] }))
    },
    formatDateOnly (val) {
      if (!val) return '-'
      const d = new Date(val)
      if (isNaN(d.getTime())) return '-'
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    async fetchAll () {
      if (!this.orderId) return
      this.loading = true
      this.hasData = false
      try {
        const [mastersRes, recordsRes] = await Promise.all([
          getOrderMasters(this.orderId),
          getOrderCheckRecords(this.orderId)
        ])
        const masterList = (mastersRes && mastersRes.data) ? mastersRes.data : []
        const recordList = (recordsRes && recordsRes.data) ? recordsRes.data : []
        this.masterList = Array.isArray(masterList) ? masterList : []
        this.recordList = Array.isArray(recordList) ? recordList : []
        this.expandedMasterIds = []
        this.hasData = this.masterList.length > 0 || this.recordList.length > 0
      } catch (e) {
        this.masterList = []
        this.recordList = []
        this.expandedMasterIds = []
        this.hasData = false
      } finally {
        this.loading = false
      }
    },
    checkTypeText (type) {
      return CHECK_TYPE_MAP[type] != null ? CHECK_TYPE_MAP[type] : (type != null ? String(type) : '-')
    },
    leaveEarlyStatusText (status) {
      return LEAVE_EARLY_STATUS_MAP[status] != null ? LEAVE_EARLY_STATUS_MAP[status] : (status != null ? String(status) : '-')
    },
    formatDateTime (val) {
      if (!val) return '-'
      const d = new Date(val)
      if (isNaN(d.getTime())) return val
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const h = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      const s = String(d.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${day} ${h}:${min}:${s}`
    }
  }
}
</script>

<style lang="less" scoped>
.order-detail-tab {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.order-detail-tab .tab-content {
  width: 100%;
  padding: 16px;
  align-self: flex-start;
}
.checkin-master-block {
  margin-bottom: 20px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
}
.checkin-master-block:last-child {
  margin-bottom: 0;
}
.checkin-master-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #fafafa;
  cursor: pointer;
  user-select: none;
}
.checkin-master-header:hover {
  background: #f0f0f0;
}
.checkin-master-expand {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}
.checkin-master-count {
  font-size: 12px;
}
.checkin-master-info {
  margin-left: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.checkin-master-name {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}
.checkin-master-mobile {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}
.checkin-empty {
  padding: 24px;
}

/* 时间轴 */
.checkin-master-body {
  border-top: 1px solid #e8e8e8;
  padding: 16px;
  max-height: 320px;
  overflow-y: auto;
}
.timeline-date-group {
  position: relative;
  padding-left: 28px;
  margin-bottom: 16px;
}
.timeline-date-group:last-child {
  margin-bottom: 0;
}
.timeline-line {
  position: absolute;
  left: 5px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e8e8e8;
  z-index: 0;
}
.timeline-date-header {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}
.timeline-date-dot {
  position: absolute;
  left: -28px;
  top: 50%;
  transform: translateY(-50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #1890ff;
  z-index: 1;
}
.timeline-date-text {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}
.timeline-record-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
}
.timeline-record-item:last-child {
  margin-bottom: 0;
}
.timeline-record-dot {
  position: absolute;
  left: -26px;
  top: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d9d9d9;
  z-index: 1;
}
.timeline-record-dot--warn {
  background: #ff4d4f;
}
.timeline-record-body {
  flex: 1;
  min-width: 0;
}
.timeline-record-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
}
.timeline-record-row--warn {
  color: #ff4d4f;
}
.timeline-record-type {
  font-weight: 500;
}
.timeline-record-time {
  color: rgba(0, 0, 0, 0.45);
}
.timeline-record-status {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}
.timeline-record-reason {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}
</style>
